/**
 * Leave request schemas — workflow only (request → approve/reject/cancel),
 * deliberately no entitlement/balance math.
 *
 * Dates are date-only (YYYY-MM-DD), inclusive range fromDate..toDate.
 */

import { z } from 'zod';
import { idSchema, iso8601Schema } from './common';

export const LEAVE_TYPES = ['ANNUAL', 'SICK', 'UNPAID', 'OTHER'] as const;
export const leaveTypeSchema = z.enum(LEAVE_TYPES);
export type LeaveType = z.infer<typeof leaveTypeSchema>;

export const LEAVE_TYPE_LABELS: Record<LeaveType, string> = {
  ANNUAL: 'Annual',
  SICK: 'Sick',
  UNPAID: 'Unpaid',
  OTHER: 'Other',
};

export const LEAVE_STATUSES = ['PENDING', 'APPROVED', 'REJECTED', 'CANCELLED'] as const;
export const leaveStatusSchema = z.enum(LEAVE_STATUSES);
export type LeaveStatus = z.infer<typeof leaveStatusSchema>;

export const LEAVE_STATUS_LABELS: Record<LeaveStatus, string> = {
  PENDING: 'Pending',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  CANCELLED: 'Cancelled',
};

const dateOnlySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD');

export const leaveRequestOutputSchema = z.object({
  id: idSchema,
  userId: idSchema,
  userName: z.string().nullable(),
  type: leaveTypeSchema,
  fromDate: dateOnlySchema,
  toDate: dateOnlySchema,
  reason: z.string().nullable(),
  status: leaveStatusSchema,
  decidedById: idSchema.nullable(),
  decidedByName: z.string().nullable(),
  decidedAt: iso8601Schema.nullable(),
  decisionNote: z.string().nullable(),
  createdAt: iso8601Schema,
  updatedAt: iso8601Schema,
});
export type LeaveRequestOutput = z.infer<typeof leaveRequestOutputSchema>;

export const createLeaveInputSchema = z.object({
  type: leaveTypeSchema,
  fromDate: dateOnlySchema,
  toDate: dateOnlySchema,
  reason: z.string().max(2000).nullable().optional(),
}).refine((v) => v.fromDate <= v.toDate, {
  message: 'fromDate must be on or before toDate',
  path: ['toDate'],
});
export type CreateLeaveInput = z.infer<typeof createLeaveInputSchema>;

export const listLeaveInputSchema = z.object({
  /** Only my own requests (all staff). Without it the queue requires leave.approve. */
  mine: z.enum(['1', 'true']).optional(),
  status: leaveStatusSchema.optional(),
  userId: idSchema.optional(),
  limit: z.coerce.number().int().min(1).max(500).default(200),
});
export type ListLeaveInput = z.infer<typeof listLeaveInputSchema>;

export const decideLeaveInputSchema = z.object({
  note: z.string().max(2000).nullable().optional(),
});
export type DecideLeaveInput = z.infer<typeof decideLeaveInputSchema>;
